import { defineStore } from 'pinia';
import axios from 'axios';
import { defaultResponseStatus, handleApiRequest } from '../functions/handleApiRequest';
import { requesterHeaders } from '../functions/appOrigin';
import type { ResponseStatus } from '../lms';
import useLmsStore from '../lms';
import useAuthStore from './auth';

interface AnalyticsEvent {
  id: string;
  page_path: string;
  page_title: string;
  referrer: string | null;
  timestamp: number;
  retryCount: number;
  processed?: boolean;
}

// Helper function to safely get runtime config in test environments
function safeUseRuntimeConfig() {
  if (process.client && typeof useRuntimeConfig === 'function') {
    try {
      return useRuntimeConfig();
    } catch {
      return null;
    }
  }
  return null;
}

interface AnalyticsStoreState {
  // Event queue management
  eventQueue: AnalyticsEvent[];
  
  // SPA navigation tracking
  currentReferrer: string | null;
  externalReferrer: string | null;
  
  // Batch configuration
  batchSize: number;
  maxQueueSize: number;
  batchTimeout: number;
  maxRetryAttempts: number;
  
  // Network and timing state
  isOnline: boolean;
  lastBatchSent: number;
  batchTimer: number | null;
  
  // Loading states
  isSendingBatch: boolean;
  isSendingSingle: boolean;
  
  // Response statuses
  batchResponseStatus: ResponseStatus;
  singleTrackResponseStatus: ResponseStatus;
  
  // Statistics
  totalEventsSent: number;
  totalBatchesSent: number;
  totalErrors: number;
  
  // Settings
  persistenceEnabled: boolean;
  storageKey: string;
}

const useAnalyticsStore = defineStore('analytics', {
  state: (): AnalyticsStoreState => ({
    // Event queue management
    eventQueue: [],
    
    // SPA navigation tracking
    currentReferrer: null,
    externalReferrer: null,
    
    // Batch configuration (dynamic values)
    batchSize: 10,
    maxQueueSize: 50,
    batchTimeout: 15000, // 15 seconds
    maxRetryAttempts: 3,
    
    // Network and timing state
    isOnline: true,
    lastBatchSent: 0,
    batchTimer: null,
    
    // Loading states
    isSendingBatch: false,
    isSendingSingle: false,
    
    // Response statuses
    batchResponseStatus: defaultResponseStatus,
    singleTrackResponseStatus: defaultResponseStatus,
    
    // Statistics
    totalEventsSent: 0,
    totalBatchesSent: 0,
    totalErrors: 0,
    
    // Settings
    persistenceEnabled: true,
    storageKey: 'analytics_queue',
  }),
  
  actions: {
    queueEvent(eventData: Partial<AnalyticsEvent>) {
      const event: AnalyticsEvent = {
        id: globalThis.crypto.randomUUID(),
        page_path: eventData.page_path || (process.client ? window.location.pathname : ''),
        page_title: eventData.page_title || (process.client ? document.title : ''),
        referrer: eventData.referrer !== undefined ? eventData.referrer : this.getReferrer(),
        timestamp: Date.now(),
        retryCount: 0,
        ...eventData,
      };

      const config = safeUseRuntimeConfig();
      if (config?.public?.enableAnalyticsLogs) {
        console.log('📝 Queuing analytics event:', event);
      }
      this.eventQueue.push(event);
      
      // Persist immediately if enabled
      if (this.persistenceEnabled) {
        this.persistQueue();
      }
      
      // Prevent queue from growing too large
      if (this.eventQueue.length > this.maxQueueSize) {
        this.eventQueue = this.eventQueue.slice(-this.maxQueueSize);
        if (this.persistenceEnabled) {
          this.persistQueue();
        }
      }

      // Check batch size before adjusting it
      const currentBatchSize = this.batchSize;
      
      // Send immediately if we hit batch size or if events are too old
      const shouldSendImmediately = 
        this.eventQueue.length >= currentBatchSize || 
        (this.eventQueue.length > 0 && this.eventQueue[0].timestamp < Date.now() - 30000);

      // Now adjust batch size for future events
      this.adjustBatchSize();

      if (shouldSendImmediately && this.isOnline && !this.isSendingBatch) {
        this.sendBatch();
      } else if (this.isOnline) {
        // Always schedule a batch if we're not currently sending and there are events
        if (!this.batchTimer && !this.isSendingBatch) {
          this.scheduleBatch();
        }
        // If we're currently sending, the completion handler will trigger the next batch
      }
    },

    adjustBatchSize() {
      const queueLength = this.eventQueue.length;
      
      if (queueLength > 30) {
        this.batchSize = 20; // Aggressive batching for heavy users
      } else if (queueLength > 15) {
        this.batchSize = 15; // Medium batching
      } else {
        this.batchSize = 10; // Standard batching
      }
    },

    async sendBatch() {
      if (this.eventQueue.length === 0 || !this.isOnline || this.isSendingBatch) {
        return;
      }

      const batchToSend = this.eventQueue.splice(0, this.batchSize);
      const remainingEvents = [...this.eventQueue];

      const config = safeUseRuntimeConfig();
      if (config?.public?.enableAnalyticsLogs) {
        console.log('📤 Sending analytics batch:', batchToSend.length, 'events');
      }
      
      const result = await handleApiRequest(
        this,
        'isSendingBatch',
        'batchResponseStatus',
        () => {
          const lmsStore = useLmsStore();
          const apiUrl = `${lmsStore.lmsApiBaseurl}analytics/batch`;
          const config = safeUseRuntimeConfig();
          if (config?.public?.enableAnalyticsLogs) {
            console.log('🌐 Making API request to:', apiUrl);
          }
          return axios.post(apiUrl, {
            events: batchToSend.map(event => ({
              page_path: event.page_path,
              page_title: event.page_title,
              referrer: event.referrer || null, // Ensure referrer field is always present
            }))
          }, {
            headers: {
              // Must be the configured public domain, never the runtime origin: in a
              // Capacitor build the latter is capacitor://localhost, which the API
              // concatenates into page_views.page_location — silent data corruption,
              // not an error. See functions/appOrigin.ts.
              ...requesterHeaders(config?.public?.siteDomain as string),
              ...useAuthStore().authHeaders
            }
          });
        }
      );

      if (result) {
        // Success! Update statistics
        this.totalEventsSent += result.processed || batchToSend.length;
        this.totalBatchesSent += 1;
        this.lastBatchSent = Date.now();
        
        // Update persistence with remaining events
        this.eventQueue = remainingEvents;
        if (this.persistenceEnabled) {
          this.persistQueue();
        }
        
        // Adjust batch size based on success
        if (result.processed === batchToSend.length) {
          this.batchSize = Math.min(this.batchSize + 2, 25);
        }

        const config = safeUseRuntimeConfig();
        if (config?.public?.enableAnalyticsLogs) {
          console.log(`Analytics batch sent: ${result.processed || batchToSend.length}/${result.total || batchToSend.length} events`, result);
        }
        
        // Continue sending if more events remain
        if (this.eventQueue.length > 0) {
          this.scheduleBatch(2000); // 2 second delay between batches
        }
      } else {
        // Failure - re-queue events with retry logic
        const retriedEvents = batchToSend.map(event => ({
          ...event,
          retryCount: event.retryCount + 1
        })).filter(event => event.retryCount <= this.maxRetryAttempts);

        this.eventQueue = [...retriedEvents, ...remainingEvents];
        if (this.persistenceEnabled) {
          this.persistQueue();
        }

        // Reduce batch size on failure
        this.batchSize = Math.max(this.batchSize - 2, 5);
        this.totalErrors += 1;

        // Retry with exponential backoff
        const retryDelay = Math.min(this.batchTimeout * Math.pow(2, batchToSend[0]?.retryCount || 0), 60000);
        if (retriedEvents.length > 0) {
          this.scheduleBatch(retryDelay);
        }
      }
    },

    scheduleBatch(delay?: number) {
      this.cancelBatchTimer();
      
      const scheduleDelay = delay || this.batchTimeout;
      this.batchTimer = window.setTimeout(() => {
        this.sendBatch();
        this.batchTimer = null;
      }, scheduleDelay);
    },

    cancelBatchTimer() {
      if (this.batchTimer) {
        clearTimeout(this.batchTimer);
        this.batchTimer = null;
      }
    },

    updateNetworkStatus() {
      if (!process.client) return;
      
      const wasOnline = this.isOnline;
      this.isOnline = navigator.onLine;
      
      if (!wasOnline && this.isOnline) {
        this.handleNetworkReconnection();
      }
    },

    handleNetworkReconnection() {
      if (this.eventQueue.length > 0) {
        const config = safeUseRuntimeConfig();
        if (config?.public?.enableAnalyticsLogs) {
          console.log('Network reconnected, sending queued analytics events');
        }
        this.scheduleBatch(1000); // Send after 1 second delay
      }
    },

    loadPersistedQueue() {
      if (!this.persistenceEnabled || !process.client) return;
      
      try {
        const stored = localStorage.getItem(this.storageKey);
        if (stored) {
          const parsedEvents = JSON.parse(stored) as AnalyticsEvent[];
          // Filter out events older than 24 hours
          const validEvents = parsedEvents.filter(
            event => event.timestamp > Date.now() - 24 * 60 * 60 * 1000
          );
          this.eventQueue = validEvents;
          
          if (validEvents.length > 0) {
            const config = safeUseRuntimeConfig();
            if (config?.public?.enableAnalyticsLogs) {
              console.log(`Loaded ${validEvents.length} persisted analytics events`);
            }
          }
        }
      } catch (error) {
        console.warn('Failed to load persisted analytics queue:', error);
      }
    },

    persistQueue() {
      if (!this.persistenceEnabled || !process.client) return;
      
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.eventQueue));
      } catch (error) {
        console.warn('Failed to persist analytics queue:', error);
      }
    },

    clearPersistedQueue() {
      if (!process.client) return;
      localStorage.removeItem(this.storageKey);
    },

    clearQueue() {
      this.eventQueue = [];
      if (this.persistenceEnabled) {
        this.clearPersistedQueue();
      }
    },

    setupEventListeners() {
      if (!process.client) return;

      // Network status changes
      window.addEventListener('online', this.updateNetworkStatus);
      window.addEventListener('offline', this.updateNetworkStatus);

      // Send remaining events before page unload
      window.addEventListener('beforeunload', () => {
        if (this.eventQueue.length > 0) {
          // Use sendBeacon for reliable delivery during page unload
          try {
            const events = this.eventQueue.map(event => ({
              page_path: event.page_path,
              page_title: event.page_title,
              referrer: event.referrer,
            }));

            navigator.sendBeacon('/api/analytics/batch', new globalThis.Blob([
              JSON.stringify({ events })
            ], { type: 'application/json' }));
            
            this.clearQueue();
          } catch {
            // Keep events persisted for next page load
          }
        }
      });

      // Page visibility changes (user switched tabs)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden' && this.eventQueue.length > 0) {
          // Send immediately when tab becomes hidden
          this.sendBatch();
        }
      });
    },

    /**
     * TDD: Set the current referrer for SPA navigation tracking
     * This is called by router hooks to track internal navigation
     */
    setCurrentReferrer(referrer: string | null) {
      this.currentReferrer = referrer;
      const config = safeUseRuntimeConfig();
      if (config?.public?.enableAnalyticsLogs) {
        console.log('🔄 SPA Referrer updated:', referrer);
      }
    },

    /**
     * TDD: Set the external referrer (captured on initial page load only)
     * This preserves the original document.referrer value
     */
    setExternalReferrer() {
      if (process.client && this.externalReferrer === null) {
        this.externalReferrer = document.referrer || null;
        const config = safeUseRuntimeConfig();
        if (config?.public?.enableAnalyticsLogs) {
          console.log('📥 External referrer captured:', this.externalReferrer);
        }
      }
    },

    /**
     * TDD: Get the appropriate referrer for the current context
     * Returns external referrer for first page, internal for subsequent navigation
     */
    getReferrer(): string | null {
      const config = safeUseRuntimeConfig();
      if (config?.public?.enableAnalyticsLogs) {
        console.log('🔍 Getting referrer - currentReferrer:', this.currentReferrer, 'externalReferrer:', this.externalReferrer);
      }
      
      // For internal SPA navigation, use the current referrer
      if (this.currentReferrer !== null) {
        return this.currentReferrer;
      }
      
      // For initial page load, use external referrer
      return this.externalReferrer;
    },

    initializeAnalytics() {
      // Capture external referrer on first initialization
      this.setExternalReferrer();
      
      // Load any persisted events from previous sessions
      this.loadPersistedQueue();
      
      // Set up event listeners
      this.setupEventListeners();
      
      // Initialize network status
      this.updateNetworkStatus();
      
      // Send any persisted events after initialization
      if (this.eventQueue.length > 0) {
        this.scheduleBatch(2000); // Send after 2 seconds on page load
      }

      const config = safeUseRuntimeConfig();
      if (config?.public?.enableAnalyticsLogs) {
        console.log('🚀 TDD Analytics initialized with', this.eventQueue.length, 'persisted events');
      }
    },

    getAnalyticsStats() {
      return {
        queueSize: this.eventQueue.length,
        totalSent: this.totalEventsSent,
        totalBatches: this.totalBatchesSent,
        totalErrors: this.totalErrors,
        isOnline: this.isOnline,
        isSending: this.isSendingBatch,
        batchSize: this.batchSize,
        lastBatchSent: this.lastBatchSent,
      };
    },
  },
});

export default useAnalyticsStore;